import { parseColumnCalc, type HaulRateBasis } from "./haulRateEvaluate";
import type {
  MfgPricingLayerBand,
  MfgPricingLayerColumn,
  MfgPricingLayerGridRow,
  MfgPricingLayerOp,
  MfgPricingLayerSourceKind,
} from "./cogsTypes";

export const COLUMN_BANDS: MfgPricingLayerBand[] = ["cogs", "wholesale", "msrp"];

export const COLUMN_BAND_LABEL: Record<MfgPricingLayerBand, string> = {
  cogs: "COGS",
  wholesale: "Wholesale",
  msrp: "MSRP",
};

/** Calculations a company can put on a column. Haul is an Add with a size rate, not its own type. */
export const COLUMN_CALC_OPTIONS = [
  { id: "add_usd", label: "Add $" },
  { id: "multiply", label: "Multiply" },
  { id: "margin_denom", label: "Margin" },
  { id: "markup", label: "Markup" },
] as const;

export const FORMULA_BASIS_OPTIONS: Array<{ id: HaulRateBasis; label: string; rateLabel: string }> = [
  { id: "building_sq_ft", label: "Square footage", rateLabel: "$ / sq ft" },
  { id: "building_length", label: "Length", rateLabel: "$ / ft" },
  { id: "flat_usd", label: "Flat amount", rateLabel: "$ each" },
  { id: "none", label: "Zero", rateLabel: "" },
];

export type ColumnCalcKind = (typeof COLUMN_CALC_OPTIONS)[number]["id"] | "labor" | "overhead";

const MODULE_KINDS = new Set(["labor", "overhead"]);
const HAUL_MODULES = new Set(["plant_haul", "lot_haul"]);

export function columnRateKind(metadata: unknown): "markup" | null {
  if (!metadata || typeof metadata !== "object" || Array.isArray(metadata)) return null;
  return (metadata as Record<string, unknown>).rate_kind === "markup" ? "markup" : null;
}

export function columnCalcFromMetadata(metadata: unknown): { basis: HaulRateBasis; rate_usd: number } | null {
  if (!metadata || typeof metadata !== "object" || Array.isArray(metadata)) return null;
  return parseColumnCalc((metadata as Record<string, unknown>).calc);
}

export function isFormulaKind(kind: ColumnCalcKind): boolean {
  return kind === "add_usd";
}

/** Haul columns stay products-only until someone chooses otherwise. */
export function columnAppliesToProductsOnly(column: {
  source_module: string | null;
  key: string;
  metadata?: unknown;
}): boolean {
  const metadata = column.metadata;
  if (metadata && typeof metadata === "object" && !Array.isArray(metadata)) {
    const applies = (metadata as Record<string, unknown>).applies_to;
    if (applies === "all") return false;
    if (applies === "products") return true;
  }
  return isShedCorHaulColumn(column);
}

export function columnCalcKind(column: {
  op: MfgPricingLayerOp;
  source_module: string | null;
  metadata?: unknown;
}): ColumnCalcKind {
  if (column.source_module && HAUL_MODULES.has(column.source_module)) return "add_usd";
  if (column.source_module && MODULE_KINDS.has(column.source_module)) {
    return column.source_module as ColumnCalcKind;
  }
  if (column.op === "multiply" && columnRateKind(column.metadata) === "markup") return "markup";
  return column.op;
}

export function isShedCorHaulColumn(column: { source_module: string | null; key: string }): boolean {
  return (
    column.source_module === "plant_haul" ||
    column.source_module === "lot_haul" ||
    column.key === "plant_haul" ||
    column.key === "lot_haul"
  );
}

export type ColumnCalcFields = {
  op: MfgPricingLayerOp;
  source_kind: MfgPricingLayerSourceKind;
  source_band: MfgPricingLayerBand | null;
  source_module: string | null;
};

export function fieldsForColumnCalc(
  kind: ColumnCalcKind,
  band: MfgPricingLayerBand,
  current?: Pick<MfgPricingLayerColumn, "op" | "source_kind" | "source_band" | "source_module">,
): ColumnCalcFields {
  if (kind === "add_usd") {
    const source_module =
      current?.source_module === "plant_haul" || current?.source_module === "lot_haul"
        ? current.source_module
        : null;
    return { op: "add_usd", source_kind: "none", source_band: null, source_module };
  }
  if (MODULE_KINDS.has(kind)) {
    return { op: "add_usd", source_kind: "none", source_band: null, source_module: kind };
  }
  if (kind === "multiply") {
    if (current && columnCalcKind(current) === "multiply" && current.source_kind !== "none") {
      return {
        op: "multiply",
        source_kind: current.source_kind,
        source_band: current.source_kind === "band_total" ? current.source_band : null,
        source_module: null,
      };
    }
    return { op: "multiply", source_kind: "running_total", source_band: null, source_module: null };
  }
  if (kind === "margin_denom" || kind === "markup") {
    const op = kind === "markup" ? "multiply" : "margin_denom";
    if (band === "wholesale") {
      return { op, source_kind: "band_total", source_band: "cogs", source_module: null };
    }
    return { op, source_kind: "running_total", source_band: null, source_module: null };
  }
  return { op: "add_usd", source_kind: "none", source_band: null, source_module: null };
}

export function columnAffectsCalculatedRows(
  columnId: string,
  rows: Array<
    Pick<MfgPricingLayerGridRow, "values_by_column_id" | "column_results">
  >,
): boolean {
  return rows.some((row) => {
    const input = row.values_by_column_id[columnId];
    if (input != null && Number.isFinite(input)) return true;
    const result = row.column_results.find((item) => item.column_id === columnId);
    return result != null && (result.input_value != null || result.result_amount != null);
  });
}

export function columnStructureChanged(
  column: Pick<
    MfgPricingLayerColumn,
    "band" | "op" | "source_kind" | "source_band" | "source_module" | "sort_order"
  >,
  next: Pick<
    MfgPricingLayerColumn,
    "band" | "op" | "source_kind" | "source_band" | "source_module" | "sort_order"
  >,
): boolean {
  return (
    column.band !== next.band ||
    column.op !== next.op ||
    column.source_kind !== next.source_kind ||
    (column.source_band ?? null) !== (next.source_band ?? null) ||
    (column.source_module ?? null) !== (next.source_module ?? null) ||
    column.sort_order !== next.sort_order
  );
}

function csvCell(value: unknown): string {
  if (value === null || value === undefined) return "";
  const text = typeof value === "string" ? value : String(value);
  if (!/[",\r\n]/.test(text)) return text;
  return `"${text.replace(/"/g, '""')}"`;
}

/** Snapshot of the loaded COGS catalog before a column change. */
export function cogsCatalogSnapshotCsv(
  columns: MfgPricingLayerColumn[],
  rows: MfgPricingLayerGridRow[],
): string {
  const active = [...columns].filter((column) => column.is_active).sort((a, b) => {
    const band = COLUMN_BANDS.indexOf(a.band) - COLUMN_BANDS.indexOf(b.band);
    if (band !== 0) return band;
    return a.sort_order - b.sort_order || a.label.localeCompare(b.label);
  });
  const headers = [
    "code",
    "name",
    "subject_kind",
    "materials_usd",
    ...active.map((column) => column.label),
    "cogs_total_usd",
    "wholesale_total_usd",
    "msrp_total_usd",
  ];
  const lines = [headers.map(csvCell).join(",")];
  for (const row of rows) {
    lines.push(
      [
        row.code,
        row.name,
        row.subject_kind,
        row.materials_cost_usd,
        ...active.map((column) => row.values_by_column_id[column.id] ?? ""),
        row.cogs_total_usd,
        row.wholesale_total_usd,
        row.msrp_total_usd,
      ]
        .map(csvCell)
        .join(","),
    );
  }
  return lines.join("\n") + "\n";
}

export function columnsInBand(
  columns: MfgPricingLayerColumn[],
  band: MfgPricingLayerBand,
): MfgPricingLayerColumn[] {
  return columns
    .filter((column) => column.band === band)
    .sort((a, b) => a.sort_order - b.sort_order || a.key.localeCompare(b.key));
}

export function columnKeyFromLabel(label: string): string {
  const key = label
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_|_$/g, "")
    .slice(0, 40);
  return key || `col_${Date.now()}`;
}
